import { t as uiText } from '../../i18n';
import type {
  SpeechToTextErrorCallback,
  SpeechToTextLevelCallback,
  SpeechToTextProvider,
  SpeechToTextTranscriptCallback
} from './SpeechToTextProvider';
import { adminSpeechToText } from '../../api/adminSpeechToText';
import { preflightMicCheck, toMicUserError, logMicError } from './micErrors';

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read audio blob'));
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Unexpected FileReader result'));
        return;
      }
      resolve(result);
    };
    reader.readAsDataURL(blob);
  });
}

export class AdminWhisperProvider implements SpeechToTextProvider {
  private mediaRecorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private chunks: BlobPart[] = [];
  private startedAtMs: number | null = null;

  private transcriptCb: SpeechToTextTranscriptCallback | null = null;
  private errorCb: SpeechToTextErrorCallback | null = null;
  private levelCb: SpeechToTextLevelCallback | null = null;

  private state: 'idle' | 'recording' | 'transcribing' | 'error' = 'idle';

  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private analyserData: Uint8Array | null = null;
  private levelRafId: number | null = null;
  private levelSmoothed: number = 0;
  private levelMax: number = 0;
  private levelSum: number = 0;
  private levelCount: number = 0;

  private maxRecordTimerId: number | null = null;
  private silenceSinceMs: number | null = null;
  private stopRequested: boolean = false;

  private static readonly MAX_RECORD_MS = 60_000;
  private static readonly SILENCE_MS = 2_000;
  private static readonly SILENCE_THRESHOLD = 0.06;
  private static readonly NO_SPEECH_MAX_THRESHOLD = 0.02;
  private static readonly NO_SPEECH_AVG_THRESHOLD = 0.012;

  constructor(
    private opts: {
      workspaceId: string;
      language?: string;
      mimeTypePreferred?: string;
    }
  ) {}

  getState() {
    return this.state;
  }

  onTranscript(cb: SpeechToTextTranscriptCallback) {
    this.transcriptCb = cb;
  }

  onError(cb: SpeechToTextErrorCallback) {
    this.errorCb = cb;
  }

  onLevel(cb: SpeechToTextLevelCallback) {
    this.levelCb = cb;
  }

  private stopLevelLoop() {
    if (this.levelRafId != null) {
      try { cancelAnimationFrame(this.levelRafId); } catch {}
      this.levelRafId = null;
    }
    this.analyserData = null;
    this.analyser = null;
    const ctx = this.audioContext;
    this.audioContext = null;
    if (ctx) {
      try { ctx.close(); } catch {}
    }
    this.levelSmoothed = 0;
  }

  private clearAutoStopTimers() {
    if (this.maxRecordTimerId != null) {
      try { window.clearTimeout(this.maxRecordTimerId); } catch {}
      this.maxRecordTimerId = null;
    }
    this.silenceSinceMs = null;
    this.stopRequested = false;
    this.levelMax = 0;
    this.levelSum = 0;
    this.levelCount = 0;
  }

  private requestStop(_reason: 'silence' | 'timeout') {
    if (this.stopRequested) return;
    if (this.state !== 'recording') return;
    this.stopRequested = true;
    try { this.stop(); } catch {}
  }

  private startLevelLoop(stream: MediaStream) {
    this.stopLevelLoop();
    if (!this.levelCb) return;

    try {
      const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!Ctx) return;
      const ctx: AudioContext = new Ctx();
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);

      const data = new Uint8Array(analyser.frequencyBinCount);
      this.audioContext = ctx;
      this.analyser = analyser;
      this.analyserData = data;
      this.levelSmoothed = 0;

      const tick = () => {
        if (this.state !== 'recording') {
          this.stopLevelLoop();
          return;
        }
        if (!this.analyser || !this.analyserData) {
          this.levelRafId = requestAnimationFrame(tick);
          return;
        }

        this.analyser.getByteTimeDomainData(this.analyserData as unknown as Uint8Array<ArrayBuffer>);
        let sum = 0;
        for (let i = 0; i < this.analyserData.length; i++) {
          const v = this.analyserData[i] / 128 - 1;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / this.analyserData.length);
        const alpha = 0.18;
        const next = this.levelSmoothed + (rms - this.levelSmoothed) * alpha;
        this.levelSmoothed = next;
        const level01 = Math.max(0, Math.min(1, next * 1.6));
        try { this.levelCb?.(level01); } catch {}
        this.levelMax = Math.max(this.levelMax, level01);
        this.levelSum += level01;
        this.levelCount += 1;

        const now = performance.now();
        if (level01 < AdminWhisperProvider.SILENCE_THRESHOLD) {
          if (this.silenceSinceMs == null) this.silenceSinceMs = now;
          const silentFor = now - this.silenceSinceMs;
          if (silentFor >= AdminWhisperProvider.SILENCE_MS) {
            this.requestStop('silence');
          }
        } else {
          this.silenceSinceMs = null;
        }

        this.levelRafId = requestAnimationFrame(tick);
      };

      this.levelRafId = requestAnimationFrame(tick);
    } catch {
      this.stopLevelLoop();
    }
  }

  async start() {
    const TAG = '🎙️ [AdminWhisper]';
    if (this.state === 'recording' || this.state === 'transcribing') {
      console.warn(`${TAG} start() 呼び出し無視 (state=${this.state})`);
      return;
    }

    console.log(`${TAG} 録音開始 workspaceId=${this.opts.workspaceId} language=${this.opts.language ?? 'ja'}`);

    const preflight = await preflightMicCheck();
    if (preflight.ok === false) {
      this.state = 'error';
      console.error(`${TAG} ❌ 事前チェック失敗: code=${preflight.code} message=${preflight.message}`);
      logMicError(preflight.code, preflight.message, { workspaceId: this.opts.workspaceId });
      this.errorCb?.({ code: preflight.code, message: preflight.message });
      this.dispose();
      return;
    }

    try {
      this.chunks = [];
      this.startedAtMs = performance.now();
      this.clearAutoStopTimers();

      console.log(`${TAG} マイク権限リクエスト中...`);
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      console.log(`${TAG} マイク権限 OK`);

      const mimeCandidates = [
        this.opts.mimeTypePreferred,
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/ogg'
      ].filter(Boolean) as string[];

      const chosenMime = mimeCandidates.find((m) => {
        try {
          return typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m);
        } catch {
          return false;
        }
      });
      console.log(`${TAG} MIMEタイプ選択: ${chosenMime ?? '(ブラウザデフォルト)'}`);

      this.mediaRecorder = new MediaRecorder(this.stream, chosenMime ? { mimeType: chosenMime } : undefined);
      this.mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) this.chunks.push(e.data);
      };
      this.mediaRecorder.onerror = () => {
        console.error(`${TAG} ❌ MediaRecorder エラー`);
        this.state = 'error';
        logMicError('RECORDER_ERROR', 'A recording error occurred', { workspaceId: this.opts.workspaceId });
        this.errorCb?.({ code: 'RECORDER_ERROR', message: uiText("A recording error occurred") });
      };

      this.mediaRecorder.start();
      this.state = 'recording';
      console.log(`${TAG} ✅ 録音中 (state=recording)`);
      this.startLevelLoop(this.stream);

      this.maxRecordTimerId = window.setTimeout(() => {
        console.warn(`${TAG} ⏱️ 最大録音時間(60秒)に達したため自動停止`);
        this.requestStop('timeout');
      }, AdminWhisperProvider.MAX_RECORD_MS);
    } catch (e: any) {
      this.state = 'error';
      const userError = toMicUserError(e);
      console.error(`${TAG} ❌ start() エラー: ${e?.name} - ${e?.message}`);
      logMicError(userError.code, userError.message, {
        workspaceId: this.opts.workspaceId,
        extra: { originalError: e?.name, originalMessage: e?.message },
      });
      this.errorCb?.(userError);
      this.dispose();
    }
  }

  async stop() {
    const TAG = '🎙️ [AdminWhisper]';
    if (this.state !== 'recording') {
      console.warn(`${TAG} stop() 呼び出し無視 (state=${this.state})`);
      return;
    }

    console.log(`${TAG} 録音停止 → 文字起こし開始`);
    this.state = 'transcribing';
    this.stopLevelLoop();
    if (this.maxRecordTimerId != null) {
      try { window.clearTimeout(this.maxRecordTimerId); } catch {}
      this.maxRecordTimerId = null;
    }

    const recorder = this.mediaRecorder;
    if (!recorder) {
      console.error(`${TAG} ❌ MediaRecorder が null（録音状態不正）`);
      this.state = 'error';
      logMicError('NO_RECORDER', 'Invalid recording state', { workspaceId: this.opts.workspaceId });
      this.errorCb?.({ code: 'NO_RECORDER', message: uiText("Invalid recording state") });
      return;
    }

    const durationMs =
      typeof this.startedAtMs === 'number'
        ? Math.min(
            AdminWhisperProvider.MAX_RECORD_MS,
            Math.max(0, Math.round(performance.now() - this.startedAtMs))
          )
        : undefined;
    console.log(`${TAG} recording duration: ${durationMs ?? 'unknown'}ms`);

    const stopped = new Promise<void>((resolve) => {
      recorder.onstop = () => resolve();
      try {
        recorder.stop();
      } catch {
        resolve();
      }
    });

    await stopped;

    const mimeType = recorder.mimeType || 'audio/webm';
    const blob = new Blob(this.chunks, { type: mimeType });
    console.log(`${TAG} 音声 Blob: size=${blob.size}bytes mimeType=${mimeType} chunks=${this.chunks.length}`);

    try {
      const levelAvg = this.levelCount > 0 ? this.levelSum / this.levelCount : 0;
      console.log(`${TAG} 音量レベル: max=${this.levelMax.toFixed(3)} avg=${levelAvg.toFixed(3)}`);

      if (
        this.levelMax < AdminWhisperProvider.NO_SPEECH_MAX_THRESHOLD &&
        levelAvg < AdminWhisperProvider.NO_SPEECH_AVG_THRESHOLD
      ) {
        console.warn(`${TAG} ⚠️ 無音判定のためスキップ (max=${this.levelMax.toFixed(3)} avg=${levelAvg.toFixed(3)})`);
        this.state = 'idle';
        logMicError('NO_SPEECH', 'No speech detected, skipped transcription', {
          workspaceId: this.opts.workspaceId,
          extra: { levelMax: this.levelMax, levelAvg, levelCount: this.levelCount, audioCtxState: this.audioContext?.state },
        });
        this.errorCb?.({ code: 'NO_SPEECH', message: uiText("No speech detected, skipped transcription") });
        return;
      }

      console.log(`${TAG} Base64エンコード中...`);
      const audioBase64 = await blobToBase64(blob);
      console.log(`${TAG} Base64エンコード完了 (length=${audioBase64.length})`);

      console.log(`${TAG} APIリクエスト送信中...`);
      const resp = await adminSpeechToText({
        workspaceId: this.opts.workspaceId,
        audioBase64,
        mimeType,
        durationMs,
        language: this.opts.language
      });

      if (resp.ok !== true) {
        const err = (resp as Extract<typeof resp, { ok: false }>).error;
        console.error(`${TAG} ❌ API エラー応答: code=${err.code} message=${err.message}`);
        this.state = 'error';
        logMicError(err.code, err.message, { workspaceId: this.opts.workspaceId, extra: { requestId: (resp as any).requestId } });
        this.errorCb?.({ code: err.code, message: err.message });
        return;
      }

      const text = resp.transcript?.trim();
      if (!text) {
        console.warn(`${TAG} ⚠️ transcript が空`);
        this.state = 'error';
        logMicError('NO_TRANSCRIPT', 'Could not recognize speech', { workspaceId: this.opts.workspaceId });
        this.errorCb?.({ code: 'NO_TRANSCRIPT', message: uiText("Could not recognize speech") });
        return;
      }

      console.log(`${TAG} ✅ 文字起こし完了: "${text}"`);
      this.state = 'idle';
      this.transcriptCb?.(text, true);
    } catch (e: any) {
      console.error(`${TAG} ❌ stop() 予期しないエラー:`, e);
      this.state = 'error';
      logMicError('TRANSCRIBE_ERROR', 'Speech recognition failed', {
        workspaceId: this.opts.workspaceId,
        extra: { error: e?.message },
      });
      this.errorCb?.({ code: 'TRANSCRIBE_ERROR', message: uiText("Speech recognition failed") });
    } finally {
      this.dispose();
    }
  }

  dispose() {
    this.stopLevelLoop();
    this.clearAutoStopTimers();
    try {
      this.mediaRecorder?.stream?.getTracks?.().forEach((t) => t.stop());
    } catch {}
    try {
      this.stream?.getTracks?.().forEach((t) => t.stop());
    } catch {}
    this.mediaRecorder = null;
    this.stream = null;
    this.chunks = [];
    this.startedAtMs = null;
    if (this.state === 'recording' || this.state === 'transcribing') {
      this.state = 'idle';
    }
  }
}
